import React, { useEffect, useState, useMemo, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { X } from "lucide-react";
import DraggableSection from "./DragbleSection";
import "./EmailDetails.css";
import AddEmailSection from "./AddEmailSection.jsx";
import EmailSectionModal from "./EmailSectionModel";
import { standardizeComponentProps } from "../../utils/ComponentPropTransformer";
import {
  useEmailSections,
  withEmailSections,
} from "../../components/Context/EmailSectionContext";

// Import standardized components from EmailModelComponents
import RightAlignImage from "./EmailModelComponents/RightAlignImage";
import LeftAlignImage from "./EmailModelComponents/LeftAlignImage";
import ThreeIconContent from "./EmailModelComponents/ThreeIconContent";
import Banner from "./EmailModelComponents/Banner";
import TitleDescription from "./EmailModelComponents/TitleDescription";
import ProductGrid from "./EmailModelComponents/ProductGrid";
import ImageTextFeature from "./EmailModelComponents/ImageTextFeature";
import HeaderImageText from "./EmailModelComponents/HeaderImageText";
import HeadingDescription from "./EmailModelComponents/HeadingDescription";
import CenteredHeading from "./EmailModelComponents/CenteredHeading";
import OfferBlock from "./EmailModelComponents/OfferBlock";
import MultiColumnLayout from "./EmailModelComponents/MultiColumnLayout";
import Button from "./EmailModelComponents/Button";
import Footer from "./EmailModelComponents/Footer";
import DynamicComponent from "./EmailModelComponents/DynamicComponent";

// Skeleton UI Component for loading state
const EmailSectionSkeleton = ({ variant = "default" }) => {
  // Different layout variations to simulate different section types
  switch(variant) {
    case "banner":
      return (
        <div className="skeleton-section" style={{
          background: "#f8f8f8",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}>
          {/* Banner style skeleton */}
          <div className="skeleton-banner" style={{ 
            height: "180px", 
            width: "100%", 
            background: "#e0e0e0", 
            borderRadius: "4px",
            position: "relative",
            overflow: "hidden"
          }}>
            <div className="skeleton-banner-text" style={{
              position: "absolute",
              bottom: "20px",
              left: "20px",
              right: "20px"
            }}>
              <div className="skeleton-title" style={{ 
                height: "24px", 
                width: "60%", 
                background: "rgba(255,255,255,0.7)", 
                borderRadius: "4px",
                marginBottom: "10px"
              }}></div>
              <div className="skeleton-line" style={{ 
                height: "16px", 
                width: "80%", 
                background: "rgba(255,255,255,0.7)", 
                borderRadius: "4px"
              }}></div>
            </div>
          </div>
        </div>
      );
      
    case "multi-column":
      return (
        <div className="skeleton-section" style={{
          background: "#f8f8f8",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}>
          {/* Multi-column style skeleton */}
          <div className="skeleton-title" style={{ 
            height: "24px", 
            width: "40%", 
            background: "#e0e0e0", 
            borderRadius: "4px",
            marginBottom: "20px",
            marginLeft: "auto",
            marginRight: "auto"
          }}></div>
          
          <div className="skeleton-columns" style={{ 
            display: "flex", 
            gap: "15px",
            justifyContent: "space-between"
          }}>
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton-column" style={{ flex: 1 }}>
                <div className="skeleton-image" style={{ 
                  height: "100px", 
                  width: "100%", 
                  background: "#e0e0e0", 
                  borderRadius: "4px",
                  marginBottom: "10px"
                }}></div>
                <div className="skeleton-title" style={{ 
                  height: "16px", 
                  width: "80%", 
                  background: "#e0e0e0", 
                  borderRadius: "4px",
                  marginBottom: "8px"
                }}></div>
                <div className="skeleton-line" style={{ 
                  height: "12px", 
                  width: "100%", 
                  background: "#e0e0e0", 
                  borderRadius: "4px",
                  marginBottom: "6px"
                }}></div>
                <div className="skeleton-line" style={{ 
                  height: "12px", 
                  width: "90%", 
                  background: "#e0e0e0", 
                  borderRadius: "4px"
                }}></div>
              </div>
            ))}
          </div>
        </div>
      );
    
    default:
      return (
        <div className="skeleton-section" style={{
          background: "#f8f8f8",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}>
          {/* Default skeleton layout */}
          <div className="skeleton-header" style={{ display: "flex", justifyContent: "space-between", marginBottom: "15px" }}>
            <div className="skeleton-title" style={{ 
              height: "24px", 
              width: "60%", 
              background: "#e0e0e0", 
              borderRadius: "4px" 
            }}></div>
            <div className="skeleton-actions" style={{ 
              height: "24px", 
              width: "80px", 
              background: "#e0e0e0", 
              borderRadius: "4px" 
            }}></div>
          </div>
          
          <div className="skeleton-content" style={{ display: "flex", gap: "20px" }}>
            <div className="skeleton-image" style={{ 
              height: "120px", 
              width: "200px", 
              background: "#e0e0e0", 
              borderRadius: "4px",
              flexShrink: 0
            }}></div>
            <div className="skeleton-text" style={{ flex: 1 }}>
              <div className="skeleton-line" style={{ 
                height: "16px", 
                width: "100%", 
                background: "#e0e0e0", 
                borderRadius: "4px",
                marginBottom: "10px" 
              }}></div>
              <div className="skeleton-line" style={{ 
                height: "16px", 
                width: "90%", 
                background: "#e0e0e0", 
                borderRadius: "4px",
                marginBottom: "10px" 
              }}></div>
              <div className="skeleton-line" style={{ 
                height: "16px", 
                width: "80%", 
                background: "#e0e0e0", 
                borderRadius: "4px" 
              }}></div>
            </div>
          </div>
        </div>
      );
  }
};

// Helper function to sanitize image URLs
const sanitizeImageUrl = (url) => {
  if (!url) return null;
  if (typeof url !== "string") return url;

  // Remove duplicate timestamp parameters
  if (url.includes("&t=")) {
    return url.split("&t=")[0];
  }
  return url;
};

// Style reference counter to track instances
let styleReferenceCount = 0;

// Style management functions
const addStyleElement = (styleId, styleContent) => {
  styleReferenceCount++;
  console.log(`[EmailDetails] Adding style reference: ${styleReferenceCount}`);

  let styleEl = document.getElementById(styleId);

  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = styleId;
    styleEl.setAttribute("data-ref-count", styleReferenceCount.toString());
    document.head.appendChild(styleEl);
    console.log(`[EmailDetails] Created new style element: ${styleId}`);
  } else {
    // Update reference count
    styleEl.setAttribute("data-ref-count", styleReferenceCount.toString());
    console.log(`[EmailDetails] Updated existing style element: ${styleId}`);
  }

  // Set the style content
  styleEl.innerHTML = styleContent;

  return styleEl;
};

const removeStyleElement = (styleId) => {
  // Decrement reference counter
  styleReferenceCount = Math.max(0, styleReferenceCount - 1);

  try {
    const styleEl = document.getElementById(styleId);

    if (styleEl) {
      // Update reference count
      styleEl.setAttribute("data-ref-count", styleReferenceCount.toString());

      // Only remove if no components are using it
      if (styleReferenceCount === 0) {
        if (styleEl.parentNode) {
          styleEl.parentNode.removeChild(styleEl);
          console.log(`[EmailDetails] Removed style element: ${styleId}`);
        }
      } else {
        console.log(
          `[EmailDetails] Keeping style element (${styleReferenceCount} refs remaining)`
        );
      }
    }
  } catch (error) {
    console.warn(
      `[EmailDetails] Error removing style element: ${error.message}`
    );
  }
};

// Accept styling props
const EmailDetailsContainer = ({
  primaryColor = "#333333",
  secondaryColor = "#a1894c",
  backgroundColor = "#ffffff",
  fontFamily = "helvetica, 'helvetica neue', arial, verdana, sans-serif",
  previewMode = false,
  emailData, // This can be used by the withEmailSections HOC
  onUpdateEmailData, // Optional callback to update parent component state
  isLoading,
}) => {
  // Create style settings object once to avoid unnecessary re-renders
  const styleSettings = useMemo(
    () => ({
      primaryColor,
      secondaryColor,
      backgroundColor,
      fontFamily,
      buttonTextColor: "#ffffff",
    }),
    [primaryColor, secondaryColor, backgroundColor, fontFamily]
  );

  // Use context for section data (array-based)
  const {
    sections,
    addSection,
    updateSection,
    removeSection,
    setAllSections,
    getSectionsObject,
  } = useEmailSections();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (emailData && Array.isArray(emailData) && emailData.length > 0) {
      setAllSections(emailData);
    }
  }, [emailData, setAllSections]);

  // Log context sections at the top level

  // Helper: Export/save sections as object (for backwards compatibility)
  const exportEmailSections = useCallback(() => {
    const sectionsObj = getSectionsObject();
    console.log("Exporting sections as object:", sectionsObj);
    return sectionsObj;
  }, [getSectionsObject]);

  // Update parent component's emailData when context changes (if callback provided)
  // useEffect(() => {
  //   if (onUpdateEmailData && sections.length > 0) {
  //     const sectionsObj = getSectionsObject();
  //     console.log("Updating parent emailData with:", sectionsObj);
  //     onUpdateEmailData(sectionsObj);
  //   }
  // }, [sections, onUpdateEmailData, getSectionsObject]);

  // Log sections when they change (for debugging)
  useEffect(() => {
    console.log("Current sections in EmailDetailsContainer:", sections);
    onUpdateEmailData(sections);
  }, [sections]);

  // Apply style changes immediately when props change
  useEffect(() => {
    // Style element ID
    const styleId = "email-global-styles";

    // Define global styles based on the current theme
    const styleContent = `
      .product-section {
        font-family: ${fontFamily} !important;
      }
      .email-content-container {
        font-family: ${fontFamily} !important;
      }
      /* Apply background color to main section tables and divs */
      /* Target tables within sections - use more specific selectors to avoid nested tables */
      .draggable-section > table,
      .draggable-section > *:not(.component-controls) > table:first-of-type,
      .product-section > table,
      .email-section > table {
        background-color: ${backgroundColor} !important;
      }
     
      .product-section h1, .product-section h2, .product-section h3, 
      .product-section h4, .product-section h5, .product-section h6,
      .banner-title,
      .section-title,
      .heading-text {
        color: ${secondaryColor} !important;
      }
      .product-section p, 
      .product-section div:not(.section-header),
      .banner-description,
      .section-description,
      .content-text,
      .description-text {
        color: ${primaryColor} !important;
      }
      .email-header p {
        color: ${primaryColor} !important;
      }
      .email-header p strong {
        color: ${secondaryColor} !important;
      }
      .product-section .description-text {
        color: ${primaryColor} !important;
      }
      .product-section .productDescription {
        color: ${primaryColor} !important;
      }
    `;

    // Add the style element
    addStyleElement(styleId, styleContent);

    return () => {
      // Clean up on unmount
      removeStyleElement(styleId);
    };
  }, [primaryColor, secondaryColor, backgroundColor, fontFamily]);

  // Drag-and-drop reorder handler
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setAllSections(items);
  };

  // Add new section
  const handleAddNewSection = (selectedSection) => {
    const newSectionId = `section-${Date.now()}`;
    console.log("Adding new section:", selectedSection.type, newSectionId);

    // Base section data with common properties
    const newSectionData = {
      id: newSectionId,
      component_name: selectedSection.type,
      title: "",
      description: "",
    };

    // Add component-specific data
    switch (selectedSection.type) {
      case "3Col_3Img":
        newSectionData.image1 = null;
        newSectionData.image2 = null;
        newSectionData.image3 = null;
        newSectionData.text1 = "";
        newSectionData.text2 = "";
        newSectionData.text3 = "";
        break;

      case "productGrid":
        newSectionData.products = Array.from({ length: 4 }, () => ({
          name: "Product Name",
          price: "₹0",
          image: null,
        }));
        break;

      case "Header_2Col_1Img_Left":
        newSectionData.image = null;
        newSectionData.price = "0.00";
        newSectionData.instock = "In Stock";
        break;

      case "Banner":
        newSectionData.image =
          "https://via.placeholder.com/800x400/e0e0e0/999999?text=Banner+Image";
        newSectionData.title = "Hey *|FNAME|*!";
        newSectionData.description = "Welcome to our newsletter";
        newSectionData.text_position = "below";
        newSectionData.text_color = "#333333";
        newSectionData.height = "300px";
        newSectionData.overlay = false;
        newSectionData.overlay_opacity = 0.5;
        newSectionData.overlay_color = "#000000";
        break;

      case "Img_circle":
      case "Img_Circle":
        newSectionData.image = null;
        newSectionData.image_placeholder = "";
        newSectionData.background_color = "#ffffff";
        newSectionData.backgroundColor = "#ffffff";
        // keep base title/description (already set above)
        break;

      case "2Col_1Img_left":
      case "2Col_1Img_right":
      case "2Col_1Img_left_H1_H2":
        newSectionData.image = null;
        break;

      case "ThreeCol_1Imgleft":
        const columnCount = 3;
        newSectionData.columnItems = Array(columnCount)
          .fill()
          .map(() => ({
            title: "",
            description: "",
            image: null,
          }));
        break;

      case "FourCol_1Imgleft":
      case "4Col_1Img_left":
      case "4Col_1Img_Left":
        newSectionData.heading = "";
        newSectionData.title = "";
        newSectionData.quantity = "";
        newSectionData.price = "";
        newSectionData.image = null;
        newSectionData.buttonText = "";
        newSectionData.buttonLink = "#";
        break;

      case "OfferDetails_H1_Code":
        newSectionData.promoCode = "SAVE20";
        break;

      case "OfferDetails_H1_Desc":
        newSectionData.title = "";
        newSectionData.description = "";
        newSectionData.link = "#";
        newSectionData.button_color = "#e11d48";
        newSectionData.text_color = "#ffffff";
        newSectionData.features = [
          {
            component_name: "Cart_Item",
            title: "View this week's offers",
            link: "#",
            cta: "View this week's offers",
          },
        ];
        break;

      case "Button":
        newSectionData.link = "#";
        newSectionData.button_color = styleSettings.primaryColor;
        newSectionData.text_color = "#ffffff";
        break;

      case "Header":
        newSectionData.image = "";
        newSectionData.title = "Company Name";
        newSectionData.description = "Your tagline here";
        newSectionData.links = [
          { text: "Home", url: "#" },
          { text: "Products", url: "#" },
          { text: "Contact", url: "#" },
        ];
        break;

      case "H1_H2_Desc":
        newSectionData.title = "Section Heading";
        newSectionData.description = "Section description text goes here";
        newSectionData.features = [
          {
            component_name: "Button",
            title: "Shop Now",
            link: "#",
          },
        ];
        break;

      case "Description_btn":
      case "Description_Btn":
        newSectionData.title = "";
        newSectionData.description = "";
        newSectionData.link = "#";
        newSectionData.button_color = "#e11d48";
        newSectionData.text_color = "#ffffff";
        newSectionData.features = [
          {
            component_name: "Cart_Item",
            title: "View this week's offers",
            link: "#",
            cta: "View Now",
          },
        ];
        break;

      case "2COL_3ROWS_Benefits":
      case "2Col_3Rows_Benefits":
        newSectionData.title = "";
        newSectionData.description = "";
        newSectionData.features = [
          {
            component_name: "Cart_Item",
            title: "",
            description: "",
            image_placeholder: "",
            link: "#",
            cta: "Buy Now",
          },
          {
            component_name: "Cart_Item",
            title: "",
            description: "",
            image_placeholder: "",
            link: "#",
            cta: "Buy Now",
          },
          {
            component_name: "Cart_Item",
            title: "",
            description: "",
            image_placeholder: "",
            link: "#",
            cta: "Buy Now",
          },
        ];
        break;

      case "Title_4Img":
      case "H1_4Img":
      case "Title_4Img_Grid":
        newSectionData.title = "";
        newSectionData.image1 = null;
        newSectionData.image2 = null;
        newSectionData.image3 = null;
        newSectionData.image4 = null;
        break;

      case "Left_Image_Right_Title_Desc_Btn":
        newSectionData.title = "Product Category";
        newSectionData.description = "Description of this product category";
        newSectionData.image =
          "https://via.placeholder.com/600x400/e0e0e0/999999?text=Category+Image";
        newSectionData.features = [
          {
            component_name: "Button",
            title: "Shop Now",
            link: "#",
          },
        ];
        break;

      case "Right_Image_Left_Title_Desc_Btn":
        newSectionData.title = "Product Category";
        newSectionData.description = "Description of this product category";
        newSectionData.image =
          "https://via.placeholder.com/600x400/e0e0e0/999999?text=Category+Image";
        newSectionData.features = [
          {
            component_name: "Button",
            title: "Shop Now",
            link: "#",
          },
        ];
        break;

      case "Footer":
        newSectionData.title = "Follow Us On";
        newSectionData.description =
          "Connect with us on social media for the latest updates.";
        newSectionData.copyright = `© ${new Date().getFullYear()} Company Name. All rights reserved.`;
        newSectionData.links = [
          { name: "Instagram", url: "#" },
          { name: "Facebook", url: "#" },
          { name: "Twitter", url: "#" },
        ];
        newSectionData.features = [
          {
            component_name: "Social Links",
            title: "Social Media",
            description: "Facebook | Instagram | LinkedIn",
          },
        ];
        break;

      case "Social Links":
        newSectionData.title = "Social Media";
        newSectionData.description = "Facebook | Instagram | LinkedIn";
        break;

      case "faq_generic":
      case "FAQ_Generic":
        newSectionData.title = "Frequently asked questions";
        newSectionData.description = "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.";
        newSectionData.faqItems = [
          {
            question: "Ipsum is simply dummy text of the printing and typesetting industry?",
            answer: "Duis convallis neque sed eros vehicula vestibulum ut a orci. Aliquam sed nibh nec metus porta porta. Integer justo nisi, venenatis quis dapibus vel, auctor efficitur nibh. Vestibulum accumsan augue eu enim vehicula, vel interdum ligula",
          },
          {
            question: "Ipsum is simply dummy text of the printing and typesetting industry?",
            answer: "Duis convallis neque sed eros vehicula vestibulum ut a orci. Aliquam sed nibh nec metus porta porta. Integer justo nisi, venenatis quis dapibus vel, auctor efficitur nibh. Vestibulum accumsan augue eu enim vehicula, vel interdum ligula",
          },
          {
            question: "Ipsum is simply dummy text of the printing and typesetting industry?",
            answer: "Duis convallis neque sed eros vehicula vestibulum ut a orci. Aliquam sed nibh nec metus porta porta. Integer justo nisi, venenatis quis dapibus vel, auctor efficitur nibh. Vestibulum accumsan augue eu enim vehicula, vel interdum ligula",
          },
        ];
        break;

      case "ecom_ingredients":
      case "Ecom_Ingredients":
        newSectionData.title = "";
        newSectionData.description = "";
        newSectionData.ingredients = [
          {
            icon: null,
            title: "Tamarind pulp",
            description:
              "Tamarind pulp is made from the fruit of the tamarind tree and is a staple in Indian cuisine. It offers a deliciously tangy flavor while being packed with antioxidants, vitamins, and minerals.",
            benefitsHeading: "Nutrition Benefits:",
            benefits:
              "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
          },
          {
            icon: null,
            title: "Tamarind pulp",
            description:
              "Tamarind pulp is made from the fruit of the tamarind tree and is a staple in Indian cuisine. It offers a deliciously tangy flavor while being packed with antioxidants, vitamins, and minerals.",
            benefitsHeading: "Nutrition Benefits:",
            benefits:
              "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
          },
        ];
        break;

      case "2Col":
        newSectionData.title = "Aliquam vitae lobortis tellus.";
        newSectionData.description = "Aenean imperdiet lectus massa, in dictum metus mattis vitae. Nullam in erat at lectus sagittis interdum nec vel velit. Donec venenatis sagittis libero, sed pretium justo laoreet vitae.\n\nDuis convallis neque sed eros vehicula vestibulum ut a orci. Aliquam sed nibh nec metus porta porta. Integer justo nisi, venenatis quis dapibus vel, auctor efficitur nibh.";
        newSectionData.image = null;
        newSectionData.layout = "text-left";
        break;

      case "3Col_1Img_center":
      case "3Col_1Img_Center":
        newSectionData.title = "Lorem ipsum dolor";
        newSectionData.bottomText = "dictum metus mattis vitae";
        newSectionData.centerImage = null;
        newSectionData.leftContent = [
          {
            icon: null,
            text: "Vivamus dolor elementum id odio sed, posuere consequat justo. Maecenas rhoncus pulvinar",
          },
          {
            icon: null,
            text: "Vivamus dolor elementum id odio sed, posuere consequat justo. Maecenas rhoncus pulvinar",
          },
        ];
        newSectionData.rightContent = [
          {
            icon: null,
            text: "Vivamus dolor elementum id odio sed, posuere consequat justo. Maecenas rhoncus pulvinar",
          },
          {
            icon: null,
            text: "Vivamus dolor elementum id odio sed, posuere consequat justo. Maecenas rhoncus pulvinar",
          },
        ];
        break;

      case "4Col_4Img_Title_price":
      case "4Col_4Img_Title_Price":
        newSectionData.title = "";
        newSectionData.products = [
          {
            image: null,
            name: "Product Name",
            price: "Price: ₹",
          },
          {
            image: null,
            name: "Product Name",
            price: "Price: ₹",
          },
          {
            image: null,
            name: "Product Name",
            price: "Price: ₹",
          },
          {
            image: null,
            name: "Product Name",
            price: "Price: ₹",
          },
        ];
        break;

      case "H1_AlignRight_Desc":
      case "H1_Align_Right_Desc":
        newSectionData.title = "Don't worry, we're here to help!";
        newSectionData.description = "Thank you for shopping with us! We're still holding on to the items you added to your cart, but only for a limited time. Complete your purchase now before the items sell out. Or get in touch with us at 9056885501 with any questions.";
        break;

      case "Header_Title_Desc":
      case "Header_Title_Description":
        newSectionData.title = "How to hang your art.";
        newSectionData.subtitle = "Don't panic. It's easier than you think.";
        newSectionData.description = "We know it can be a little intimidating, so we've gone ahead and answered all the questions you've ever had about how to properly get your beautifully-framed art and photos on your walls. That gallery wall of your dreams is closer than you knew!";
        newSectionData.buttonText = "Educate Me";
        newSectionData.buttonLink = "#";
        break;

      case "H1_Desc_4":
      case "H1_Desc4":
        newSectionData.title = "Hey *|FNAME|*!";
        newSectionData.description = "This is a quick reminder that you still have a 10% discount code to use at You can use it on any product you find in the store, including our bestselling extension.\n\nAll our products are designed to make it easier than ever for you to enjoy fantastic hair every single day.\n\nSo forget about bad hair days and start making your mornings easier!";
        break;

      case "H1_Desc_5":
      case "H1_Desc5":
        newSectionData.title = "Hey *|FNAME|*!";
        newSectionData.description = "Just a quick reminder that your 20% coupon code expires in less than 24 hours.\n\n**This is one deal you don't want to miss, so get in quick!**";
        newSectionData.couponInstruction = "Just find something you like then use the code below at the checkout";
        newSectionData.couponCode = "RETURN20-PREVIEW";
        newSectionData.buttonText = "Use My Discount >>";
        newSectionData.buttonLink = "#";
        newSectionData.closingText = "Happy shopping!\n\nBest Regards,\nVanmay Sutras Team";
        break;

      case "H1_Desc_6":
      case "H1_Desc6":
        newSectionData.title = "HI *|FNAME|*!";
        newSectionData.description = "Happy news! You still have your **15% DISCOUNT** code for the next **24 HOURS**";
        newSectionData.couponInstruction = "Just use your special code at checkout";
        newSectionData.couponCode = "WELCOME15-PREVIEW";
        newSectionData.button1Text = "Take 15% Off";
        newSectionData.button1Link = "#";
        newSectionData.inspirationalMessage = "With comfy, cute, and adjustable designs, take positive affirmations into every moment of your day.\n\nDiscover the power of inspiring words that feed the soul when no one else is around.";
        newSectionData.button2Text = "Inspire Me";
        newSectionData.button2Link = "#";
        newSectionData.closingText = "I hope your day is filled with joy.";
        break;

      case "H1_H2_Desc_4":
      case "H1_H2_Desc4":
        newSectionData.greeting = "Hey *|FNAME|*!";
        newSectionData.title = "Thank you so much for your purchase!";
        newSectionData.subtitle = "";
        newSectionData.description = "We're delighted that you're finding just what you want for you and your dog at Vanmay Sutras.\n\nWe are passionate about dogs, and nothing makes our day more than helping a customer find the perfect item for their pooch.\n\nYou're the reason we do what we do, and we like to take care of our top shoppers.";
        newSectionData.discountIntro = "As a thank you gift, here is a 20% discount code:";
        newSectionData.couponCode = "RETURN20-PREVIEW";
        newSectionData.discountWarning = "It's only valid for 48 hours, so please be quick!";
        newSectionData.buttonText = "Get 20% Off Today >>";
        newSectionData.buttonLink = "#";
        newSectionData.closingText = "In the meantime, feel free to shoot us a message with any questions or concerns.\n\nWe'd love to hear from you!";
        newSectionData.signature = "Best Regards,\nVanmay Sutras Team";
        break;

      case "OfferDetails_Desc_3":
      case "OfferDetails_Desc3":
        newSectionData.introMessage = "Go and find something today, and remember to use your promo code:";
        newSectionData.promoCode = "XXXXXX";
        newSectionData.urgencyMessage = "But hurry-in 12 hours this code will expire and you'll have to pay the full price again!";
        newSectionData.buttonText = "Save 10% >>";
        newSectionData.buttonLink = "#";
        newSectionData.closingText = "Enjoy!";
        newSectionData.signature = "Vanmay Sutras Team";
        newSectionData.postscript = "P.S. Get some inspiration from our bestsellers, check them out below!";
        break;

      default:
        // No special setup needed for other components
        break;
    }

    // Standardize the section data to ensure consistent data structure
    const standardizedSectionData = standardizeComponentProps(newSectionData);
    console.log("Adding standardized section:", standardizedSectionData);

    // Add to context
    addSection(newSectionId, standardizedSectionData);

    // Also update parent component if callback exists
    if (onUpdateEmailData) {
      // Get current sections as object including the new one
      setTimeout(() => {
        const updatedSectionsObj = getSectionsObject();
        console.log("Updating parent with new sections:", updatedSectionsObj);
        onUpdateEmailData(updatedSectionsObj);
      }, 0);
    }

    setIsModalOpen(false);
  };

  // Update section
  const handleUpdateSection = (updatedData, sectionId) => {
    if (!sectionId) return;

    console.log("Updating section:", sectionId, updatedData);

    // Ensure component_name is preserved
    const updatedWithComponentName = {
      ...updatedData,
      component_name:
        updatedData.component_name ||
        sections.find((s) => s.id === sectionId)?.component_name,
    };

    updateSection(sectionId, updatedWithComponentName);
  };

  return (
    <div className="email-content-container" style={{ fontFamily }}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppable-sections">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
             
            >
              {sections.map((section, index) => {
                // Log the props for each DynamicComponent
                return (
                  <Draggable
                    key={section.id}
                    draggableId={section.id}
                    index={index}
                    isDragDisabled={previewMode}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="draggable-section"
                      >
                        {!previewMode && (
                          <button
                            className="remove-section-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSection(section.id);
                              // Also update parent component if callback exists
                              if (onUpdateEmailData) {
                                setTimeout(() => {
                                  const updatedSectionsObj = getSectionsObject();
                                  onUpdateEmailData(updatedSectionsObj);
                                }, 0);
                              }
                            }}
                            title="Remove section"
                          >
                            <X size={16} />
                          </button>
                        )}
                        <DynamicComponent
                          {...section}
                          previewMode={previewMode}
                          onUpdate={(updatedData) => {
                            console.log(
                              "Section update requested:",
                              section.id,
                              updatedData
                            );
                            handleUpdateSection(updatedData, section.id);
                          }}
                          styleSettings={styleSettings}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {isLoading && (
        <div className="skeleton-container">
          {/* Show a dynamic number of skeletons based on existing sections or at least 2 */}
          <EmailSectionSkeleton variant="banner" />
          {sections.length > 0 ? (
            // Show skeletons matching the number of existing sections (up to 3)
            Array.from({ length: Math.min(sections.length, 3) }).map((_, index) => (
              <EmailSectionSkeleton 
                key={`skeleton-${index}`} 
                variant={index % 3 === 0 ? "default" : index % 3 === 1 ? "multi-column" : "banner"} 
              />
            ))
          ) : (
            // Show at least one more skeleton if no sections exist yet
            <EmailSectionSkeleton variant="multi-column" />
          )}
        </div>
      )}

      {!previewMode && (
        <>
         <div className="container">
      <div className="row justify-content-center">
        <div className="col-12 col-sm-10 col-md-8 col-lg-6">
          <div className="empty-section">
            <div className="placeholder-icon">
              <img 
                src="/addsection.svg" 
                alt="Add Section" 
                className="img-fluid w-200 h-200"
              />
            </div>
            
            <h3 className="empty-title">Add more sections</h3>
            
            <p className="empty-description">
              Showcasing product benefits can help you increase sales. We have a variety of components for you to select from.
            </p>
            
            <button 
              className="add-section-btn"
              onClick={() => setIsModalOpen(true)}
            >
              Add Section
            </button>
          </div>
        </div>
      </div>
      <EmailSectionModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onAddSection={handleAddNewSection}
          />
    </div>
         
         
        </>
      )}
      {/* Example export button for demonstration (remove in production) */}
      {/* <button onClick={() => console.log(exportEmailSections())}>Export Sections</button> */}
    </div>
  );
};

// Export wrapped component to ensure it always has access to sections context
export default EmailDetailsContainer;
